import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Form, Modal, Table } from "react-bootstrap";
import {
  getClaim,
  changeClaimStatus,
  rollbackClaimStatus,
} from "../../services/apiClient.js";
import { formatDate, formatDateTime } from "../../utils/date.js";
import TabHeader from "./TabHeader.jsx";

// The status grid — one row per inspection cycle (unlimited reinspections), columns
// across the R→I→Q→A/D→C progression. This is the legacy ViewStatus matrix rebuilt
// on the event-sourced engine: the current cycle's reachable next steps are clickable
// arrows in their column; to go back, use Roll back (append-only, never a delete).
// Reinspection is not a button here — it starts from the Inspection tab (a new
// inspection appends a fresh "Re Requested" row). See docs/status-rules.md.

const COLUMNS = ["R", "I", "Q", "A", "D", "C"];
const NAME = { R: "Requested", I: "Inspected", Q: "Quoted", A: "Accepted", D: "Declined", C: "Completed" };

const cycleLabel = (seq) => (seq === 1 ? "Initial" : `Reinspection ${seq - 1}`);

function ClaimStatusTab({ claimId, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [steps, setSteps] = useState(1);
  const [hideRollbacks, setHideRollbacks] = useState(false);

  async function load() {
    try {
      setData(await getClaim(claimId));
    } catch {
      setError("Unable to load status.");
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  // Decline reasons — 1.0's EXACT five (DetailsEdit rblDeclinedReasons), no
  // free text, same as 20 years of habit (phase-1 rule: duplicate 1.0).
  const DECLINE_REASONS = ["Withdrawn", "Cash Settled", "Claim Declined", "Non Competitive", "Allocated to MPS"];
  const [declineShow, setDeclineShow] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  async function forward(code, reason = null) {
    setError("");
    if (code === "D" && !reason) {
      setDeclineReason("");
      setDeclineShow(true); // 1.0's five fixed reasons — picked in the modal
      return;
    }
    try {
      await changeClaimStatus(claimId, { to: code, reason });
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to change status.");
    }
  }

  async function rollback() {
    setError("");
    // The top `steps` live events; popping a reinspection's R deletes its inspection.
    const active = data?.active || [];
    const top = active.slice(Math.max(0, active.length - steps));
    const reinsp = top.filter((e) => e.status_code === "R" && e.inspection_id != null).length;
    let msg = `Roll back ${steps} status change${steps > 1 ? "s" : ""}?`;
    if (reinsp > 0) {
      msg += `\n\nThis also deletes ${reinsp} reinspection${reinsp > 1 ? "s" : ""} — its inspection record${
        reinsp > 1 ? "s" : ""
      } will be removed.`;
    }
    if (!window.confirm(msg)) return;
    try {
      await rollbackClaimStatus(claimId, steps);
      setSteps(1);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to roll back.");
    }
  }

  if (!data) {
    return error ? (
      <div className="alert alert-danger">{error}</div>
    ) : (
      <p className="text-muted">Loading…</p>
    );
  }

  const {
    current_status_code: cur,
    current_status_name: curName,
    allowed_transitions: allowed = [],
    can_rollback,
    rollback_depth,
    cycles = [],
    history = [],
  } = data;
  const allowedSet = new Set(allowed.map((t) => t.code));

  // One matrix cell. In the live (last) cycle, a reachable next status renders a
  // clickable arrow in its column; the current status's cell is highlighted.
  function renderCell(cycle, code, isLast) {
    const date = cycle.dates[code];
    const isCurrent = isLast && code === cur;
    const canAct = isLast && allowedSet.has(code);
    return (
      <td key={code} className={`text-center ${isCurrent ? "table-primary" : ""}`}>
        {date ? (
          <div className={isCurrent ? "fw-semibold" : undefined}>{formatDate(date)}</div>
        ) : null}
        {canAct ? (
          <Button
            size="sm"
            variant={code === "D" ? "outline-danger" : "outline-primary"}
            className={date ? "mt-1" : undefined}
            onClick={() => forward(code)}
          >
            → {NAME[code]}
          </Button>
        ) : !date ? (
          <span className="text-muted">—</span>
        ) : null}
      </td>
    );
  }

  return (
    <>
      <TabHeader
        title="Status & History"
        help="status"
        actions={
          <span className="small">
            Current: <Badge bg="primary">{curName || cur}</Badge>
          </span>
        }
      />
      {error ? <div className="alert alert-danger">{error}</div> : null}

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="text-uppercase fw-semibold small mb-0" style={{ letterSpacing: "0.06em" }}>Status</h6>
          </div>

          <Table bordered hover responsive size="sm" className="mb-0 align-middle text-nowrap">
            <thead className="table-surface">
              <tr>
                <th>Cycle</th>
                {COLUMNS.map((code) => (
                  <th key={code} className="text-center">
                    {NAME[code]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cycles.map((cycle, i) => (
                <tr key={cycle.seq}>
                  <td className="text-muted">{cycleLabel(cycle.seq)}</td>
                  {COLUMNS.map((code) => renderCell(cycle, code, i === cycles.length - 1))}
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
            {can_rollback ? (
              <>
                <span className="text-muted small">Roll back</span>
                <Form.Control
                  size="sm"
                  type="number"
                  min={1}
                  max={rollback_depth}
                  value={steps}
                  onChange={(e) =>
                    setSteps(Math.max(1, Math.min(rollback_depth, Number(e.target.value) || 1)))
                  }
                  style={{ width: 64 }}
                  data-hint={`Up to ${rollback_depth}`}
                />
                <span className="text-muted small">change{steps > 1 ? "s" : ""}</span>
                <Button size="sm" variant="outline-secondary" onClick={rollback}>
                  Roll back
                </Button>
              </>
            ) : null}
            <span className="text-muted small ms-auto">
              Reinspections are started from the Inspection tab.
            </span>
          </div>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between align-items-center">
        <h6 className="text-uppercase fw-semibold small mb-1" style={{ letterSpacing: "0.06em" }}>History</h6>
        <Form.Check
          type="checkbox"
          id="hide-rollbacks"
          label="Hide rollbacks"
          checked={hideRollbacks}
          onChange={(e) => setHideRollbacks(e.target.checked)}
        />
      </div>
      <Table striped bordered hover size="sm" className="mb-0 align-middle">
        <thead className="table-surface">
          <tr>
            <th>Event</th>
            <th>When</th>
            <th>By</th>
            <th>Reason / Note</th>
          </tr>
        </thead>
        <tbody>
          {history
            .filter((h) => !hideRollbacks || h.kind !== "rollback")
            .slice()
            .reverse()
            .map((h) => (
              <tr key={h.id}>
                <td>
                  {h.kind === "rollback" ? (
                    <span className="text-secondary">↩ Rolled back to {h.status_name}</span>
                  ) : (
                    <Badge bg="secondary">{h.status_name}</Badge>
                  )}
                </td>
                <td>{formatDateTime(h.occurred_at)}</td>
                <td>{h.changed_by_name || "-"}</td>
                <td>{h.reason || h.note || "-"}</td>
              </tr>
            ))}
        </tbody>
      </Table>
      <Modal show={declineShow} onHide={() => setDeclineShow(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Reason for declining</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ThemedSelect
            autoFocus
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          >
            <option value="">— choose a reason —</option>
            {DECLINE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </ThemedSelect>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            size="sm"
            disabled={!declineReason}
            onClick={() => {
              setDeclineShow(false);
              forward("D", declineReason);
            }}
          >
            Decline job
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setDeclineShow(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ClaimStatusTab;
